import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  isNavigationItemActive,
  resolveActiveNavigationItem,
} from "../../navigation/navigationConfig";

function joinClasses(...values) {
  return values.filter(Boolean).join(" ");
}

export default function TopMegaMenu({ groups = [], location, onNavigate }) {
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const buttonRefs = useRef(new Map());
  const [openGroupKey, setOpenGroupKey] = useState("");
  const [panelLeft, setPanelLeft] = useState(0);

  const activeGroupKey = useMemo(() => {
    let best = { key: "", score: -1 };
    for (const group of groups) {
      const match = resolveActiveNavigationItem(group.items || [], location);
      if (match.score > best.score) {
        best = { key: group.key, score: match.score };
      }
    }
    return best.key || String(groups[0]?.key || "");
  }, [groups, location]);

  const activeGroup =
    groups.find((group) => group.key === (openGroupKey || activeGroupKey)) || groups[0] || null;

  useEffect(() => {
    setOpenGroupKey("");
  }, [location?.pathname, location?.search]);

  useEffect(() => {
    if (!openGroupKey) return undefined;

    function handlePointerDown(event) {
      if (!rootRef.current?.contains(event.target)) {
        setOpenGroupKey("");
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setOpenGroupKey("");
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openGroupKey]);

  useEffect(() => {
    if (!openGroupKey) return undefined;

    function updatePanelPosition() {
      const rootNode = rootRef.current;
      const panelNode = panelRef.current;
      const buttonNode = buttonRefs.current.get(openGroupKey);
      if (!rootNode || !panelNode || !buttonNode) return;

      const rootRect = rootNode.getBoundingClientRect();
      const buttonRect = buttonNode.getBoundingClientRect();
      const panelWidth = panelNode.offsetWidth || 320;
      const buttonCenter = buttonRect.left - rootRect.left + buttonRect.width / 2;
      const desiredLeft = buttonCenter - panelWidth / 2;
      const maxLeft = Math.max(0, rootNode.offsetWidth - panelWidth);

      setPanelLeft(Math.min(Math.max(0, desiredLeft), maxLeft));
    }

    updatePanelPosition();
    window.addEventListener("resize", updatePanelPosition);
    return () => {
      window.removeEventListener("resize", updatePanelPosition);
    };
  }, [openGroupKey, groups]);

  if (!Array.isArray(groups) || groups.length === 0) {
    return null;
  }

  return (
    <div ref={rootRef} className="relative z-10">
      <nav
        aria-label="Hoofdmenu"
        className="overflow-x-auto overscroll-x-contain scroll-smooth"
      >
        <div className="flex min-w-max items-center gap-2">
          {groups.map((group) => {
            const isOpen = openGroupKey === group.key;
            const isActive = group.key === activeGroupKey;
            return (
              <button
                key={group.key}
                ref={(node) => {
                  if (node) {
                    buttonRefs.current.set(group.key, node);
                  } else {
                    buttonRefs.current.delete(group.key);
                  }
                }}
                type="button"
                onClick={() =>
                  setOpenGroupKey((current) => (current === group.key ? "" : group.key))
                }
                className={joinClasses(
                  "inline-flex shrink-0 items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition",
                  isOpen || isActive
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                )}
                aria-expanded={isOpen}
                aria-haspopup="menu"
              >
                <span>{group.label}</span>
                <ChevronDown
                  className={joinClasses(
                    "h-4 w-4 transition-transform",
                    isOpen ? "rotate-180" : ""
                  )}
                />
              </button>
            );
          })}
        </div>
      </nav>

      {openGroupKey && activeGroup ? (
        <div
          ref={panelRef}
          className="absolute top-full z-20 w-[min(320px,calc(100vw-2rem))] pt-3"
          style={{ left: `${panelLeft}px` }}
        >
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">
                    {activeGroup.label}
                  </h2>
                  {activeGroup.description ? (
                    <p className="mt-1 text-xs text-slate-600">
                      {activeGroup.description}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => setOpenGroupKey("")}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Sluiten
                </button>
              </div>
            </div>

            <div className="max-h-[70vh] space-y-0.5 overflow-y-auto p-2.5">
              {(activeGroup.items || []).map((item) => {
                const active = isNavigationItemActive(item, location);
                const Icon = item.icon;
                return (
                  <button
                    key={item.to}
                    type="button"
                    onClick={() => {
                      setOpenGroupKey("");
                      onNavigate?.(item.to);
                    }}
                    className={joinClasses(
                      "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left transition",
                      active
                        ? "bg-blue-50 text-blue-900"
                        : "text-slate-800 hover:bg-slate-50"
                    )}
                  >
                    {Icon ? (
                      <span
                        className={joinClasses(
                          "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
                          active ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                    ) : null}

                    <span className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="truncate text-[13px] font-medium leading-5">
                        {item.label}
                      </span>
                      {item.badge ? (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                          {item.badge}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
